/**
 * <s-studio-merge>: Trust · Behind the scenes "One studio". Scrubbed by scroll while the card crosses the
 * viewport (no pinning): the scattered theme demos are pulled into the centre (--m), fade out (--co) and The4
 * logo grows in with its glow (--l); [data-done] shows the line under it. [data-scrub] marks the animated mode;
 * without it (reduced motion, or before JS) the CSS defaults are the final state. Hovering the card shows one
 * team member at a time behind the logo ([data-on] on a [data-person]), chosen by the pointer's band across the card.
 */
class SStudioMerge extends HTMLElement {
  #raf = 0;
  #io = null;
  #on = false;

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  };

  #people = [];
  #card = null;
  #hoverAc = null;

  /** Team reveal: the card is split into equal bands across its width; the band under the pointer shows one person. */
  #bindPeople() {
    this.#people = [...this.querySelectorAll('[data-person]')];
    this.#card = this.closest('.proof-team') || this;
    if (!this.#people.length || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#hoverAc = new AbortController();
    const opts = { signal: this.#hoverAc.signal };
    this.#card.addEventListener('pointermove', (event) => {
      const box = this.#card.getBoundingClientRect();
      const band = Math.floor(((event.clientX - box.left) / box.width) * this.#people.length);
      this.#show(Math.min(this.#people.length - 1, Math.max(0, band)));
    }, opts);
    this.#card.addEventListener('pointerleave', () => this.#show(-1), opts);
  }

  #show(index) {
    this.#people.forEach((person, i) => person.toggleAttribute('data-on', i === index));
  }

  connectedCallback() {
    this.#bindPeople();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.setAttribute('data-scrub', '');
    this.#update();
    this.#io = new IntersectionObserver(([entry]) => this.#listen(entry.isIntersecting), { rootMargin: '100px 0px' });
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#hoverAc?.abort();
    this.#io?.disconnect();
    this.#listen(false);
    cancelAnimationFrame(this.#raf);
  }

  #listen(on) {
    if (on === this.#on) return;
    this.#on = on;
    if (on) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onScroll, { passive: true });
      this.#update();
    } else {
      window.removeEventListener('scroll', this.#onScroll);
      window.removeEventListener('resize', this.#onScroll);
    }
  }

  #update() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    // 0 when the card's top meets the bottom of the screen, 1 when it reaches the top quarter.
    const p = Math.min(1, Math.max(0, (vh - rect.top) / (vh * 0.75)));
    const span = (from, to) => Math.min(1, Math.max(0, (p - from) / (to - from)));
    const ease = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
    this.style.setProperty('--m', ease(span(0.12, 0.8)).toFixed(4));
    this.style.setProperty('--co', (1 - span(0.7, 0.9)).toFixed(4));
    this.style.setProperty('--l', ease(span(0.72, 0.95)).toFixed(4));
    this.toggleAttribute('data-done', p > 0.92);
  }
}

if (!customElements.get('s-studio-merge')) customElements.define('s-studio-merge', SStudioMerge);
